/**
 * VerifyEmailPage - Email Verification Flow
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Loading states, error states, success redirect handling.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { applyActionCode, checkActionCode } from "firebase/auth";
import { Loader2 } from "lucide-react";

// Hooks & Contexts
import { useTranslation } from "@/shared/hooks";
import { useStore } from "@/stores";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button } from "@/shared/ui/components";

// Lib
import { auth } from "@/shared/lib/firebase";

// Constants
import { AppRoutes } from "@/shared/constants";

type VerifyStatus =
  | "checking"
  | "success"
  | "verified_redirect"
  | "error"
  | "missing_code";

export default function VerifyEmailPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const rootStore = useStore();
  const { authStore } = rootStore;
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const oobCode = params.get("oobCode");
  const mode = params.get("mode");

  const [status, setStatus] = useState<VerifyStatus>("checking");
  const [errorType, setErrorType] = useState<"expired" | "invalid" | "generic">(
    "generic",
  );
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!oobCode) {
        if (mounted) {
          setStatus("verified_redirect");
        }
        return;
      }
      try {
        const info = await checkActionCode(auth, oobCode);
        if (mounted && info.data?.email) {
          setUserEmail(info.data.email);
        }

        await applyActionCode(auth, oobCode);
        if (mounted) {
          setStatus("success");
        }
      } catch (e) {
        if (mounted) {
          const code = (e as { code?: string })?.code || "";
          if (code === "auth/expired-action-code") {
            setStatus("error");
            setErrorType("expired");
          } else if (code === "auth/invalid-action-code") {
            setStatus("verified_redirect");
          } else {
            setStatus("error");
            setErrorType("generic");
          }
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [oobCode, mode]);

  const handleResendVerification = async () => {
    if (!userEmail || resending) return;

    setResending(true);
    setResendError(false);
    setResendSuccess(false);

    try {
      const token = await authStore.getIdToken();
      if (token) {
        await rootStore.api.auth.resendEmailVerification(token);
        setResendSuccess(true);
      } else {
        setResendError(true);
      }
    } catch {
      setResendError(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <BasePage
      title={t("emailConfirmedTitle")}
      description="Verify your email address"
      maxWidth="sm"
      centerContent
      showHeader={false}
      showFooter={false}
    >
      {/* Success State */}
      {(status === "success" || status === "verified_redirect") && (
        <Card
          variant="default"
          padding="md"
          className="bg-emerald-50 border-emerald-200"
        >
          <Text variant="h4" className="text-emerald-800 mb-2">
            {t("emailConfirmedTitle")}
          </Text>
          <Text variant="body" className="text-emerald-700 mb-4">
            {t("emailConfirmedBody")}
          </Text>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("logIntoAccount")}
          </Button>
        </Card>
      )}

      {/* Checking State */}
      {status === "checking" && (
        <Box className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600 mx-auto mb-4" />
          <Text variant="body" color="primary">
            {t("pleaseWait")}
          </Text>
        </Box>
      )}

      {/* Missing Code State */}
      {status === "missing_code" && (
        <Card
          variant="default"
          padding="md"
          className="bg-yellow-50 border-yellow-200"
        >
          <Text variant="h4" className="text-yellow-800 mb-2">
            {t("verificationMissingCodeTitle")}
          </Text>
          <Text variant="body" className="text-yellow-700 mb-4">
            {t("verificationMissingCodeBody")}
          </Text>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("backToLogin")}
          </Button>
        </Card>
      )}

      {/* Error State */}
      {status === "error" && (
        <Card
          variant="default"
          padding="md"
          className="bg-red-50 border-red-200"
        >
          <Text variant="h4" className="text-red-800 mb-2">
            {t("verificationFailedTitle")}
          </Text>
          <Text variant="body" className="text-red-700 mb-4">
            {errorType === "expired"
              ? t("verificationExpired")
              : errorType === "invalid"
                ? t("verificationInvalid")
                : t("verificationFailed")}
          </Text>

          {/* Resend option for expired links */}
          {userEmail && errorType === "expired" && (
            <Box className="mb-4 p-4 bg-white rounded border border-red-100">
              <Text variant="body" className="text-sm text-gray-700 mb-3">
                {(t as unknown as (k: string, v: string) => string)(
                  "resendVerificationHint",
                  userEmail,
                )}
              </Text>
              {resendSuccess ? (
                <Text
                  variant="body"
                  className="text-sm text-emerald-600 font-medium"
                >
                  {t("verificationResent")}
                </Text>
              ) : resendError ? (
                <Text
                  variant="body"
                  className="text-sm text-red-600 font-medium mb-2"
                >
                  {t("resendFailed")}
                </Text>
              ) : null}
              {!resendSuccess && (
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={handleResendVerification}
                  disabled={resending}
                  loading={resending}
                >
                  {resending ? t("pleaseWait") : t("resendVerificationEmail")}
                </Button>
              )}
            </Box>
          )}

          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate(AppRoutes.login)}
          >
            {t("backToLogin")}
          </Button>
        </Card>
      )}
    </BasePage>
  );
}
